import { useEffect, useState } from "react"
import { X, MessageSquare } from "lucide-react"

import "./toastAnimation.css"
import { cn } from "../lib/utils"
import { createPortal } from "react-dom"
import { useFullscreenHost } from "@vc/viewmodels/screen/useFullscreenHost"

export interface Toast {
  id: string
  message: string
  senderName?: string
  senderAvatar?: string
  duration?: number
}

interface ToastProps {
  toast: Toast
  onDismiss: (id: string) => void
}

export function ToastItem({ toast, onDismiss }: ToastProps) {
  const [isExiting, setIsExiting] = useState(false)

  useEffect(() => {
    const duration = toast.duration ?? 3000
    const timer = setTimeout(() => {
      setIsExiting(true)
      setTimeout(() => onDismiss(toast.id), 300)
    }, duration)

    return () => clearTimeout(timer)
  }, [toast.id, toast.duration, onDismiss])

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-xl bg-[#202124] px-4 py-3 shadow-2xl ring-1 ring-white/10 transition-all duration-300 max-w-xs min-w-[150px]",
        isExiting ? "opacity-0 -translate-x-4" : "opacity-100 translate-x-0"
      )}
    >
      {/* Notification Bell Icon with Animation */}
      <div className="relative shrink-0">
        <div className="h-10 w-10 rounded-full bg-sky-500/20 flex items-center justify-center">
          <MessageSquare className="h-5 w-5 text-sky-400 animate-[swing_1s_ease-in-out]" />
        </div>
      </div>

      {/* Avatar */}
      {/* <div
        className="h-10 w-10 rounded-full flex items-center justify-center text-sm font-semibold text-slate-800 shrink-0"
        style={{ backgroundColor: getAvatarColor(toast.senderName || "User") }}
      >
        {(toast.senderName || "U").charAt(0).toUpperCase()}
      </div> */}

      {/* Content */}
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-semibold text-white truncate">
            {toast.senderName || "New Message"}
          </span>
        </div>
        <p className="text-sm text-slate-300 truncate">
          {toast.message}
        </p>
      </div>

      {/* Close button */}
      <button
        type="button"
        onClick={() => {
          setIsExiting(true)
          setTimeout(() => onDismiss(toast.id), 300)
        }}
        className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-white/10 hover:text-white transition-colors shrink-0 mt-1"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

export function ToastContainer({
  toasts,
  onDismiss,
  onClearAll,
  clearAllLabel = "Clear all",
}: {
  toasts: Toast[]
  onDismiss: (id: string) => void
  /** Close every toast at once; offered while two or more are showing. */
  onClearAll?: () => void
  clearAllLabel?: string
}) {
  // Rendered inside whatever owns the fullscreen layer. While a shared screen
  // or a maximised tile is fullscreen, that element is the only thing painted,
  // so a toast left in the normal tree reports its outcome off screen — which
  // is indistinguishable from the action never having run.
  const host = useFullscreenHost()

  const container = (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
      {onClearAll && toasts.length > 1 && (
        <button
          onClick={onClearAll}
          className="pointer-events-auto self-end rounded-full bg-[#202124] px-3 py-1 text-xs font-semibold text-white/90 shadow-lg ring-1 ring-white/10 hover:bg-[#2c2d30]"
        >
          {clearAllLabel} ({toasts.length})
        </button>
      )}
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem toast={toast} onDismiss={onDismiss} />
        </div>
      ))}
    </div>
  )

  // Left in place when nothing (or the whole page) is fullscreen: same tree,
  // same stacking, no behaviour change.
  if (!host || host === document.body) return container
  return createPortal(container, host)
}
